import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import {
  Plus,
  Trash2,
  Copy,
  ChevronRight,
  ChevronDown,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SLAB_GRID = "1.4fr 1fr 1fr 1fr 60px";

function fmtNum(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!isFinite(n)) return "";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function CostSlabsTable({ slabs, modelLabel, onCreate, onUpdate, onDelete, onDuplicate }) {
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid={`cost-slabs-${modelLabel}`}>
      <div
        className="grid gap-2 text-[11px] font-bold uppercase tracking-[0.12em] text-slate-700 px-3 h-10 items-center bg-slate-100/70 border-b border-slate-200"
        style={{ gridTemplateColumns: SLAB_GRID }}
      >
        <div>Cost Slab</div>
        <div>Cost per Key</div>
        <div>Cost per Credit</div>
        <div>No. of Credits</div>
        <div className="text-center">+</div>
      </div>
      {slabs.length === 0 ? (
        <div className="px-3 py-3 flex items-center justify-between border-b border-slate-100">
          <span className="text-xs text-slate-400">No slabs yet — add one →</span>
          <button
            onClick={onCreate}
            className="h-7 px-2 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium flex items-center gap-1"
            data-testid={`cost-add-slab-${modelLabel}`}
          >
            <Plus className="h-3.5 w-3.5" /> Add slab
          </button>
        </div>
      ) : (
        slabs.map((s) => {
          const cpk = parseFloat(s.cost_per_key) || 0;
          const cpc = parseFloat(s.cost_per_credit) || 0;
          const credits = cpc > 0 ? cpk / cpc : 0;
          return (
            <div
              key={s.id}
              className="grid items-center gap-2 px-3 h-12 border-b border-slate-100 hover:bg-slate-50/60 group"
              style={{ gridTemplateColumns: SLAB_GRID }}
              data-testid={`cost-slab-row-${s.id}`}
            >
              <Input
                defaultValue={s.slab_name || ""}
                onBlur={(e) => { if (e.target.value !== (s.slab_name || "")) onUpdate(s.id, { slab_name: e.target.value }); }}
                placeholder="0–100k"
                className="h-8 text-sm"
                data-testid={`cost-slab-name-${s.id}`}
              />
              <Input
                type="number"
                step="0.01"
                defaultValue={s.cost_per_key ?? ""}
                onBlur={(e) => {
                  const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                  if (v !== (s.cost_per_key ?? 0)) onUpdate(s.id, { cost_per_key: v });
                }}
                placeholder="0.00"
                className="h-8 text-sm"
                data-testid={`cost-slab-cpk-${s.id}`}
              />
              <Input
                type="number"
                step="0.01"
                defaultValue={s.cost_per_credit ?? ""}
                onBlur={(e) => {
                  const v = e.target.value === "" ? 0 : parseFloat(e.target.value);
                  if (v !== (s.cost_per_credit ?? 0)) onUpdate(s.id, { cost_per_credit: v });
                }}
                placeholder="0.00"
                className="h-8 text-sm"
                data-testid={`cost-slab-cpc-${s.id}`}
              />
              <div
                className="h-8 px-2 rounded-md bg-mint-50 border border-mint-100 text-emerald-800 text-sm font-mono flex items-center"
                title="Auto-calculated: cost per key ÷ cost per credit"
                data-testid={`cost-slab-credits-${s.id}`}
              >
                {credits ? fmtNum(credits) : <span className="text-slate-400">—</span>}
              </div>
              <div className="flex items-center justify-center gap-0.5">
                <button
                  onClick={() => onDuplicate(s.id)}
                  className="opacity-0 group-hover:opacity-100 h-7 w-7 rounded-md text-slate-400 hover:text-blue-600 hover:bg-sky-50 flex items-center justify-center transition-all"
                  data-testid={`cost-slab-duplicate-${s.id}`}
                  title="Duplicate"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onDelete(s.id)}
                  className="opacity-0 group-hover:opacity-100 h-7 w-7 rounded-md text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-all"
                  data-testid={`cost-slab-delete-${s.id}`}
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })
      )}
      {slabs.length > 0 && (
        <div className="px-3 py-2 border-t border-slate-100">
          <button
            onClick={onCreate}
            className="h-7 px-2 rounded-md bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-medium flex items-center gap-1"
            data-testid={`cost-add-slab-bottom-${modelLabel}`}
          >
            <Plus className="h-3.5 w-3.5" /> Add slab
          </button>
        </div>
      )}
    </div>
  );
}

function ModelGroup({ agent, slabs, apis, isOpen, onToggle, onCreate, onUpdate, onDelete, onDuplicate }) {
  const poweredByNames = (agent.powered_by || [])
    .map((id) => apis.find((a) => a.id === id)?.name)
    .filter(Boolean)
    .join(", ");
  const total = slabs.length;
  const label = `${agent.model_name || "—"}${poweredByNames ? ` (${poweredByNames})` : ""}`;
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid={`cost-model-${agent.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 h-11 bg-mint-50/40 hover:bg-mint-50/70 border-b border-slate-100"
      >
        {isOpen ? <ChevronDown className="h-4 w-4 text-emerald-700" /> : <ChevronRight className="h-4 w-4 text-emerald-700" />}
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-sm font-semibold text-slate-800">{label}</span>
        <span className="ml-auto text-[11px] text-slate-500 px-2 py-0.5 rounded-md bg-white border border-slate-200">
          {total} slab{total === 1 ? "" : "s"}
        </span>
      </button>
      {isOpen && (
        <div className="p-3">
          <CostSlabsTable
            slabs={slabs}
            modelLabel={agent.id}
            onCreate={() => onCreate(agent.id)}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
          />
        </div>
      )}
    </div>
  );
}

function SubSubGroup({ subSub, agentsList, slabsByAgent, apis, openModel, toggleModel, isOpen, onToggle, onCreate, onUpdate, onDelete, onDuplicate }) {
  const total = agentsList.reduce((acc, a) => acc + (slabsByAgent.get(a.id) || []).length, 0);
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid={`cost-subsub-${subSub || "blank"}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 h-11 bg-peach-50/60 hover:bg-peach-50 border-b border-slate-100"
      >
        {isOpen ? <ChevronDown className="h-4 w-4 text-amber-800" /> : <ChevronRight className="h-4 w-4 text-amber-800" />}
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        <span className="text-sm font-semibold text-slate-800">{subSub || "—"}</span>
        <span className="ml-auto text-[11px] text-slate-500 px-2 py-0.5 rounded-md bg-white border border-slate-200">
          {agentsList.length} model{agentsList.length === 1 ? "" : "s"} · {total} slabs
        </span>
      </button>
      {isOpen && (
        <div className="p-3 space-y-2">
          {agentsList.map((a) => (
            <ModelGroup
              key={a.id}
              agent={a}
              slabs={slabsByAgent.get(a.id) || []}
              apis={apis}
              isOpen={!!openModel[a.id]}
              onToggle={() => toggleModel(a.id)}
              onCreate={onCreate}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SubAgentGroup({ subAgent, subSubGroups, agentsBySubSub, slabsByAgent, apis, openSubSub, toggleSubSub, openModel, toggleModel, isOpen, onToggle, onCreate, onUpdate, onDelete, onDuplicate }) {
  const totalAgents = subSubGroups.reduce((acc, ss) => acc + (agentsBySubSub.get(ss) || []).length, 0);
  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white" data-testid={`cost-sub-${subAgent || "blank"}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 h-12 bg-lilac-50/60 hover:bg-lilac-50 border-b border-slate-100"
      >
        {isOpen ? <ChevronDown className="h-4 w-4 text-purple-700" /> : <ChevronRight className="h-4 w-4 text-purple-700" />}
        <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
        <span className="text-base font-semibold text-slate-800">{subAgent || "—"}</span>
        <span className="ml-auto text-[11px] text-slate-500 px-2 py-0.5 rounded-md bg-white border border-slate-200">
          {totalAgents} model{totalAgents === 1 ? "" : "s"}
        </span>
      </button>
      {isOpen && (
        <div className="p-3 space-y-2">
          {subSubGroups.map((ss) => (
            <SubSubGroup
              key={ss || "__blank__"}
              subSub={ss}
              agentsList={agentsBySubSub.get(ss) || []}
              slabsByAgent={slabsByAgent}
              apis={apis}
              openModel={openModel}
              toggleModel={toggleModel}
              isOpen={!!openSubSub[`${subAgent}::${ss}`]}
              onToggle={() => toggleSubSub(`${subAgent}::${ss}`)}
              onCreate={onCreate}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onDuplicate={onDuplicate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SortableTab({ id, label, count, active, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 30 : undefined,
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <button
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      data-testid={`cost-tab-${id}`}
      className={`h-9 px-4 rounded-xl text-sm font-medium whitespace-nowrap transition-colors cursor-grab active:cursor-grabbing ${
        active ? "btn-brand" : "text-slate-600 hover:bg-slate-50"
      }`}
    >
      {label}
      <span className={`ml-2 inline-block text-[11px] px-1.5 py-0.5 rounded-md ${
        active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
      }`}>
        {count}
      </span>
    </button>
  );
}

export default function Cost() {
  const [agents, setAgents] = useState([]);
  const [apis, setApis] = useState([]);
  const [slabs, setSlabs] = useState([]);
  const [tabOrder, setTabOrder] = useState([]);
  const [activeTab, setActiveTab] = useState("__all__");
  const [openSub, setOpenSub] = useState({});
  const [openSubSub, setOpenSubSub] = useState({});
  const [openModel, setOpenModel] = useState({});
  const [loading, setLoading] = useState(true);

  const tabSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = async () => {
    try {
      setLoading(true);
      const [a, p, s, t] = await Promise.all([
        api.get("/agents"),
        api.get("/apis"),
        api.get("/cost-slabs"),
        api.get("/settings/agent-tabs"),
      ]);
      setAgents(a.data);
      setApis(p.data);
      setSlabs(s.data);
      setTabOrder(Array.isArray(t.data) ? t.data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Build hierarchy keyed by agent_name → sub_agent → sub_sub_agent → [agents]
  const tree = useMemo(() => {
    const orderIndex = new Map(tabOrder.map((name, i) => [name, i]));
    const byAgent = new Map(); // agent_name -> sub_agent -> sub_sub -> [agents]
    for (const a of agents) {
      const an = a.agent_name || "";
      if (!byAgent.has(an)) byAgent.set(an, new Map());
      const subMap = byAgent.get(an);
      const sa = a.sub_agent || "";
      if (!subMap.has(sa)) subMap.set(sa, new Map());
      const subSubMap = subMap.get(sa);
      const ss = a.sub_sub_agent || "";
      if (!subSubMap.has(ss)) subSubMap.set(ss, []);
      subSubMap.get(ss).push(a);
    }
    const agentList = Array.from(byAgent.keys()).filter(Boolean).sort((x, y) => {
      const ix = orderIndex.has(x) ? orderIndex.get(x) : Number.POSITIVE_INFINITY;
      const iy = orderIndex.has(y) ? orderIndex.get(y) : Number.POSITIVE_INFINITY;
      if (ix !== iy) return ix - iy;
      return x.localeCompare(y);
    });
    const slabsByAgent = new Map();
    for (const s of slabs) {
      if (!slabsByAgent.has(s.agent_id)) slabsByAgent.set(s.agent_id, []);
      slabsByAgent.get(s.agent_id).push(s);
    }
    return { byAgent, agentList, slabsByAgent };
  }, [agents, slabs, tabOrder]);

  const visibleAgentNames = activeTab === "__all__" ? tree.agentList : [activeTab];

  const handleTabDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = tree.agentList.indexOf(active.id);
    const newIdx = tree.agentList.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const next = arrayMove(tree.agentList, oldIdx, newIdx);
    setTabOrder(next);
    try { await api.put("/settings/agent-tabs", { order: next }); }
    catch { toast.error("Failed to save tab order"); }
  };

  const handleCreateSlab = async (agentId) => {
    try {
      const { data } = await api.post("/cost-slabs", {
        agent_id: agentId,
        slab_name: "",
        cost_per_key: 0,
        cost_per_credit: 0,
        order: (tree.slabsByAgent.get(agentId) || []).length,
      });
      setSlabs((prev) => [...prev, data]);
    } catch { toast.error("Add slab failed"); }
  };

  const handleUpdateSlab = async (id, patch) => {
    try {
      const { data } = await api.patch(`/cost-slabs/${id}`, patch);
      setSlabs((prev) => prev.map((x) => (x.id === id ? data : x)));
    } catch { toast.error("Update failed"); }
  };

  const handleDeleteSlab = async (id) => {
    try {
      await api.delete(`/cost-slabs/${id}`);
      setSlabs((prev) => prev.filter((x) => x.id !== id));
      toast.success("Slab removed");
    } catch { toast.error("Delete failed"); }
  };

  const handleDuplicateSlab = async (id) => {
    try {
      await api.post(`/cost-slabs/${id}/duplicate`);
      await load();
      toast.success("Slab duplicated");
    } catch { toast.error("Duplicate failed"); }
  };

  const toggleSub = (k) => setOpenSub((p) => ({ ...p, [k]: !p[k] }));
  const toggleSubSub = (k) => setOpenSubSub((p) => ({ ...p, [k]: !p[k] }));
  const toggleModel = (k) => setOpenModel((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="space-y-6" data-testid="cost-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Wallet className="h-6 w-6 text-emerald-600" /> Cost
          </h2>
          <p className="text-sm text-slate-500">Define cost slabs per model · No. of credits = cost per key ÷ cost per credit</p>
        </div>
      </div>

      {/* Top tabs (agent names, draggable) */}
      {!loading && tree.agentList.length > 0 && (
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-2xl p-1 overflow-x-auto" data-testid="cost-tabs">
          <button
            onClick={() => setActiveTab("__all__")}
            data-testid="cost-tab-all"
            className={`h-9 px-4 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === "__all__" ? "btn-brand" : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            All agents
            <span className={`ml-2 inline-block text-[11px] px-1.5 py-0.5 rounded-md ${
              activeTab === "__all__" ? "bg-white/20 text-white" : "bg-slate-100 text-slate-500"
            }`}>
              {tree.agentList.length}
            </span>
          </button>
          <DndContext sensors={tabSensors} collisionDetection={closestCenter} onDragEnd={handleTabDragEnd}>
            <SortableContext items={tree.agentList} strategy={horizontalListSortingStrategy}>
              {tree.agentList.map((a) => {
                const subMap = tree.byAgent.get(a) || new Map();
                let count = 0;
                subMap.forEach((subSubMap) => subSubMap.forEach((arr) => { count += arr.length; }));
                return (
                  <SortableTab
                    key={a}
                    id={a}
                    label={a}
                    count={count}
                    active={activeTab === a}
                    onClick={() => setActiveTab(a)}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      )}

      {loading ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-sm text-slate-500">Loading…</div>
      ) : tree.agentList.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
          <p className="text-sm text-slate-500">No agents found — add some in the Agents catalog first.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleAgentNames.map((agentName) => {
            const subMap = tree.byAgent.get(agentName) || new Map();
            const subAgents = Array.from(subMap.keys()).sort((x, y) => (x || "").localeCompare(y || ""));
            return (
              <div key={agentName} className="space-y-3">
                {activeTab === "__all__" && (
                  <h3 className="font-display text-lg font-semibold text-slate-900 px-1">{agentName}</h3>
                )}
                {subAgents.map((sa) => {
                  const subSubMap = subMap.get(sa) || new Map();
                  const subSubGroups = Array.from(subSubMap.keys()).sort((x, y) => (x || "").localeCompare(y || ""));
                  const agentsBySubSub = new Map();
                  for (const ss of subSubGroups) agentsBySubSub.set(ss, subSubMap.get(ss));
                  const subKey = `${agentName}::${sa}`;
                  return (
                    <SubAgentGroup
                      key={subKey}
                      subAgent={sa}
                      subSubGroups={subSubGroups}
                      agentsBySubSub={agentsBySubSub}
                      slabsByAgent={tree.slabsByAgent}
                      apis={apis}
                      openSubSub={openSubSub}
                      toggleSubSub={toggleSubSub}
                      openModel={openModel}
                      toggleModel={toggleModel}
                      isOpen={!!openSub[subKey]}
                      onToggle={() => toggleSub(subKey)}
                      onCreate={handleCreateSlab}
                      onUpdate={handleUpdateSlab}
                      onDelete={handleDeleteSlab}
                      onDuplicate={handleDuplicateSlab}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
